/**
 * Shared modal shell — fixed-position backdrop + centered dialog with
 * header (title + X close button), scrollable body, and optional footer.
 *
 * Lifts the visual pattern from src/components/disposal/ToolPickerModal.jsx
 * and grafts on the four-mechanism close coverage from
 * src/shared/components/PhotoGalleryPanel.jsx (X button + backdrop click +
 * Escape key + browser-back).
 *
 * Browser-back uses pushState + popstate (Phase 2C-fixup migration from
 * the prior replace-only nav). On open, the modal pushes a sentinel
 * history entry { tmgModalOpen: true } with NO URL change (third arg to
 * pushState is the empty string, which means "stay at current URL"). On
 * popstate (browser-back), the listener calls onClose. All other close
 * paths (X / backdrop / Escape) route through handleClose → history.back()
 * → popstate → onClose, giving a single close-path source-of-truth in the
 * default (closeOnBrowserBack=true) configuration.
 *
 * The replace-only pattern this replaces was overwriting the current
 * history entry, so the back button popped the user to the previous
 * actual route entry rather than closing the modal — that's what
 * triggered this fixup. PhotoGalleryPanel still uses replace-only and
 * shares the same back-button bug; intentionally not migrated here per
 * the "don't refactor existing modals" decision (will inherit the fix
 * when eventually moved to ModalShell).
 *
 * The X-bubble bug class — where the X click bubbles to the backdrop and
 * fires the close logic twice — is prevented NOT by the nav pattern but
 * by two structural guards that must remain intact:
 *   - The dialog <div> has onClick={(e) => e.stopPropagation()} so any
 *     click inside the dialog (X, body, footer) doesn't reach the
 *     backdrop.
 *   - The backdrop onClick discriminates via e.target === e.currentTarget
 *     so only direct backdrop clicks trigger close.
 *
 * When closeOnBrowserBack is false, no pushState happens, no popstate
 * listener attaches, and handleClose calls onClose directly — matching
 * the pre-Phase-2A semantics for callers who opt out of browser-back
 * coverage. (No current consumer opts out, but the prop is preserved
 * for forward compat.)
 *
 * No focus trap, animation, or portal — out of scope per Phase 2A spec.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import './ModalShell.css';

export default function ModalShell({
  isOpen,
  onClose,
  title,
  children,
  footer,
  closeOnBackdrop = true,
  closeOnEscape = true,
  // Default OFF: the open effect's raw window.history.pushState collides with
  // react-router v6 history (clobbers its {usr,key,idx} state), which then fires
  // spurious popstates that the unconditional listener closes the modal on.
  // With this false, the browser-back effect/listener/cleanup early-return and
  // stay dormant. Re-enable only via a router-native reimplementation — never raw
  // history.pushState/back.
  closeOnBrowserBack = false,
  className = '',
  // Optional controls for the header's right-hand side, rendered just before
  // the × (P2-5 Phase 3: the field record's three-dot menu). Absent — every
  // other caller — the header renders exactly as it did: title, then ×.
  headerActions = null,
}) {
  // Tracks whether the mouse press STARTED on the backdrop itself. A drag that
  // begins inside an input and releases on the backdrop fires a click whose
  // target is the backdrop (common ancestor) — without this, that would
  // spuriously dismiss. Only a press+release both on the backdrop closes.
  const downOnBackdrop = useRef(false);

  // Single user-driven close path. When browser-back is wired, route
  // through history.back() so the popstate listener fires onClose() —
  // giving a single source of truth for state cleanup. When opted out,
  // call onClose() directly (no sentinel to pop).
  const handleClose = useCallback(() => {
    if (closeOnBrowserBack && window.history.state?.tmgModalOpen === true) {
      window.history.back();
    } else {
      onClose();
    }
  }, [closeOnBrowserBack, onClose]);

  // Push sentinel on open; listen for popstate; pop sentinel on cleanup
  // if it's still present (defensive against parent-driven closes that
  // didn't go through handleClose).
  useEffect(() => {
    if (!isOpen || !closeOnBrowserBack) return undefined;
    if (window.history.state?.tmgModalOpen !== true) {
      window.history.pushState({ tmgModalOpen: true }, '');
    }
    const onPopState = () => onClose();
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      if (window.history.state?.tmgModalOpen === true) {
        window.history.back();
      }
    };
  }, [isOpen, closeOnBrowserBack, onClose]);

  // Escape key.
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closeOnEscape, handleClose]);

  if (!isOpen) return null;

  // Backdrop click: close only if click target is the backdrop itself
  // (e.target === e.currentTarget guards against bubbled clicks from
  // dialog children — belt-and-suspenders alongside the dialog's
  // stopPropagation handler below). See the docstring's X-bubble note.
  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget && downOnBackdrop.current) {
      handleClose();
    }
  };

  const dialogClassName = `modal-shell-dialog${className ? ` ${className}` : ''}`;

  const closeButton = (
    <button
      type="button"
      className="modal-shell-close"
      onClick={handleClose}
      aria-label="Close"
    >
      &times;
    </button>
  );

  return (
    <div
      className="modal-shell-backdrop"
      onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className={dialogClassName}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="modal-shell-header">
          <h2 className="modal-shell-title">{title}</h2>
          {headerActions != null ? (
            // Grouped with the × so the header's space-between keeps the title
            // left and both controls right.
            <div className="modal-shell-header-actions">
              {headerActions}
              {closeButton}
            </div>
          ) : closeButton}
        </div>
        <div className="modal-shell-body">{children}</div>
        {footer != null && (
          <div className="modal-shell-footer">{footer}</div>
        )}
      </div>
    </div>
  );
}
